import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { GlassCard } from "@/components/common/GlassCard";
import { PageHeader } from "@/components/common/PageHeader";
import { StatusPill } from "@/components/common/StatusPill";
import { LoadingState } from "@/components/common/States";
import { PlayerSubNav } from "@/components/common/PlayerSubNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Pencil, Trophy, Target, ArrowDownRight, Trash2, Plus, Ruler, Weight, Timer, CalendarDays, Hash, Users } from "lucide-react";
import { toast } from "sonner";

const SLOTS = [
  {
    key: "best_case",
    label: "Ceiling",
    subtitle: "Aspirational outcome — what it looks like when it goes right",
    icon: Trophy,
    accent: "from-emerald-400/15 to-emerald-300/0",
    ring: "ring-emerald-300/30",
    text: "text-emerald-200",
    pill: "emerald",
  },
  {
    key: "realistic",
    label: "Realistic",
    subtitle: "What we'd bet on today — most likely career",
    icon: Target,
    accent: "from-cyan-400/15 to-cyan-300/0",
    ring: "ring-cyan-300/30",
    text: "text-cyan-200",
    pill: "cyan",
  },
  {
    key: "floor",
    label: "Floor",
    subtitle: "Worst case if things break — still NFL viable",
    icon: ArrowDownRight,
    accent: "from-amber-400/15 to-amber-300/0",
    ring: "ring-amber-300/30",
    text: "text-amber-200",
    pill: "amber",
  },
];

const EMPTY_FORM = {
  comp_name: "",
  comp_position: "",
  comp_team: "",
  comp_draft_year: "",
  comp_draft_round: "",
  comp_draft_pick: "",
  height: "",
  weight: "",
  forty_yard: "",
  play_style: "",
  role_projection: "",
  why_fits: "",
  similarities: "",
  differences: "",
  headshot_url: "",
};

function useResolvedPlayerId() {
  const { user } = useAuth();
  const { playerId } = useParams();
  return playerId || user?.player_id;
}

function StatBlock({ icon: Icon, label, value, testId }) {
  return (
    <div className="glass p-3" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-3 text-[10px] uppercase tracking-wider">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="text-1 text-sm font-mono mt-1">{value || "—"}</div>
    </div>
  );
}

function CompCard({ slot, comp, isAdmin, onEdit, onDelete }) {
  const Icon = slot.icon;
  const draftLine = comp
    ? [
        comp.comp_draft_year ? `${comp.comp_draft_year}` : null,
        comp.comp_draft_round ? `R${comp.comp_draft_round}` : null,
        comp.comp_draft_pick ? `Pick #${comp.comp_draft_pick}` : null,
      ].filter(Boolean).join(" · ")
    : null;

  return (
    <GlassCard className="col-span-12 lg:col-span-4 p-6 relative overflow-hidden flex flex-col" testId={`comp-card-${slot.key}`}>
      <div className={`absolute -top-24 -right-24 h-72 w-72 rounded-full pointer-events-none bg-gradient-to-br ${slot.accent}`} />
      <div className="flex items-center justify-between relative">
        <div className="flex items-center gap-3">
          <div className={`h-9 w-9 rounded-lg bg-white/5 ring-1 ${slot.ring} grid place-items-center ${slot.text}`}>
            <Icon className="h-4 w-4" />
          </div>
          <div>
            <div className={`text-[10px] uppercase tracking-[0.18em] ${slot.text}`}>{slot.label}</div>
            <div className="text-3 text-[11px]">{slot.subtitle}</div>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1.5">
            <Button
              data-testid={`comp-edit-${slot.key}`}
              size="sm"
              variant="ghost"
              className="text-2 hover:text-white h-8 px-2"
              onClick={onEdit}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
            {comp && (
              <Button
                data-testid={`comp-delete-${slot.key}`}
                size="sm"
                variant="ghost"
                className="text-rose-300/70 hover:text-rose-300 h-8 px-2"
                onClick={onDelete}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>
        )}
      </div>

      {!comp ? (
        <div className="mt-8 text-center grow flex flex-col items-center justify-center" data-testid={`comp-empty-${slot.key}`}>
          <div className="h-12 w-12 rounded-full bg-white/[0.04] ring-1 ring-white/10 grid place-items-center mb-3">
            <Users className="h-5 w-5 text-3" />
          </div>
          <div className="text-1 text-sm font-medium">No comparison set</div>
          <div className="text-3 text-xs mt-1">{isAdmin ? "Add an NFL comp to fill this slot." : "Your advisor hasn't set a comp here yet."}</div>
          {isAdmin && (
            <Button data-testid={`comp-add-${slot.key}`} onClick={onEdit} className="cta-primary mt-4">
              <Plus className="h-4 w-4" /> Add comp
            </Button>
          )}
        </div>
      ) : (
        <div className="mt-5 relative grow flex flex-col">
          <div>
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className="text-1 text-xl font-semibold leading-tight">{comp.comp_name}</div>
              {comp.comp_position && <StatusPill tone={slot.pill}>{comp.comp_position}</StatusPill>}
            </div>
            <div className="text-2 text-sm">{comp.comp_team || "—"}</div>
            {draftLine && (
              <div className="text-3 text-[11px] mt-1 flex items-center gap-1.5">
                <CalendarDays className="h-3 w-3" /> {draftLine}
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">
            <StatBlock icon={Ruler} label="Height" value={comp.height} testId={`comp-${slot.key}-height`} />
            <StatBlock icon={Weight} label="Weight" value={comp.weight} testId={`comp-${slot.key}-weight`} />
            <StatBlock icon={Timer} label="40 yd" value={comp.forty_yard} testId={`comp-${slot.key}-forty`} />
          </div>

          {(comp.play_style || comp.role_projection) && (
            <div className="mt-4 space-y-2">
              {comp.play_style && (
                <div>
                  <div className="text-3 text-[10px] uppercase tracking-wider">Play style</div>
                  <div className="text-2 text-sm leading-relaxed mt-1">{comp.play_style}</div>
                </div>
              )}
              {comp.role_projection && (
                <div>
                  <div className="text-3 text-[10px] uppercase tracking-wider">Role projection</div>
                  <div className="text-2 text-sm leading-relaxed mt-1">{comp.role_projection}</div>
                </div>
              )}
            </div>
          )}

          {(comp.why_fits || comp.similarities || comp.differences) && (
            <div className="mt-4 space-y-3">
              {comp.why_fits && (
                <div className="glass p-3">
                  <div className={`text-[10px] uppercase tracking-wider ${slot.text}`}>Why it fits</div>
                  <div className="text-1 text-sm leading-relaxed mt-1">{comp.why_fits}</div>
                </div>
              )}
              {comp.similarities && (
                <div className="glass p-3">
                  <div className="text-3 text-[10px] uppercase tracking-wider">Similarities</div>
                  <div className="text-2 text-sm leading-relaxed mt-1">{comp.similarities}</div>
                </div>
              )}
              {comp.differences && (
                <div className="glass p-3">
                  <div className="text-3 text-[10px] uppercase tracking-wider">Differences</div>
                  <div className="text-2 text-sm leading-relaxed mt-1">{comp.differences}</div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </GlassCard>
  );
}

function CompEditor({ open, onOpenChange, slot, comp, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (comp) {
        setForm({
          comp_name: comp.comp_name || "",
          comp_position: comp.comp_position || "",
          comp_team: comp.comp_team || "",
          comp_draft_year: comp.comp_draft_year ?? "",
          comp_draft_round: comp.comp_draft_round ?? "",
          comp_draft_pick: comp.comp_draft_pick ?? "",
          height: comp.height || "",
          weight: comp.weight || "",
          forty_yard: comp.forty_yard || "",
          play_style: comp.play_style || "",
          role_projection: comp.role_projection || "",
          why_fits: comp.why_fits || "",
          similarities: comp.similarities || "",
          differences: comp.differences || "",
          headshot_url: comp.headshot_url || "",
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, comp]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.comp_name.trim()) {
      toast.error("Comp name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        comp_draft_year: form.comp_draft_year ? Number(form.comp_draft_year) : null,
        comp_draft_round: form.comp_draft_round ? Number(form.comp_draft_round) : null,
        comp_draft_pick: form.comp_draft_pick ? Number(form.comp_draft_pick) : null,
      };
      await onSave(payload);
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-strong border-white/10 max-w-2xl text-1" data-testid={`comp-editor-${slot?.key}`}>
        <DialogHeader>
          <DialogTitle>{comp ? "Edit" : "Add"} {slot?.label} Comp</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-12 gap-3 max-h-[60vh] overflow-y-auto pr-1">
          <div className="col-span-12 md:col-span-6">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Player Name *</Label>
            <Input data-testid="comp-form-name" value={form.comp_name} onChange={(e) => update("comp_name", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Position</Label>
            <Input data-testid="comp-form-position" value={form.comp_position} onChange={(e) => update("comp_position", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
          <div className="col-span-6 md:col-span-3">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Team</Label>
            <Input data-testid="comp-form-team" value={form.comp_team} onChange={(e) => update("comp_team", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
          <div className="col-span-4">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Draft Year</Label>
            <Input data-testid="comp-form-year" type="number" value={form.comp_draft_year} onChange={(e) => update("comp_draft_year", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1 font-mono" />
          </div>
          <div className="col-span-4">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Round</Label>
            <Input data-testid="comp-form-round" type="number" value={form.comp_draft_round} onChange={(e) => update("comp_draft_round", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1 font-mono" />
          </div>
          <div className="col-span-4">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Pick</Label>
            <Input data-testid="comp-form-pick" type="number" value={form.comp_draft_pick} onChange={(e) => update("comp_draft_pick", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1 font-mono" />
          </div>
          <div className="col-span-4">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Height</Label>
            <Input data-testid="comp-form-height" value={form.height} onChange={(e) => update("height", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1 font-mono" placeholder={'6\'2"'} />
          </div>
          <div className="col-span-4">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Weight</Label>
            <Input data-testid="comp-form-weight" value={form.weight} onChange={(e) => update("weight", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1 font-mono" placeholder="220 lbs" />
          </div>
          <div className="col-span-4">
            <Label className="text-3 text-[10px] uppercase tracking-wider">40-yard</Label>
            <Input data-testid="comp-form-forty" value={form.forty_yard} onChange={(e) => update("forty_yard", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1 font-mono" placeholder="4.78" />
          </div>
          <div className="col-span-12">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Play Style</Label>
            <Textarea data-testid="comp-form-playstyle" rows={2} value={form.play_style} onChange={(e) => update("play_style", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
          <div className="col-span-12">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Role Projection</Label>
            <Textarea data-testid="comp-form-role" rows={2} value={form.role_projection} onChange={(e) => update("role_projection", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
          <div className="col-span-12">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Why this fits</Label>
            <Textarea data-testid="comp-form-why" rows={2} value={form.why_fits} onChange={(e) => update("why_fits", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Similarities</Label>
            <Textarea data-testid="comp-form-similarities" rows={2} value={form.similarities} onChange={(e) => update("similarities", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
          <div className="col-span-12 md:col-span-6">
            <Label className="text-3 text-[10px] uppercase tracking-wider">Differences</Label>
            <Textarea data-testid="comp-form-differences" rows={2} value={form.differences} onChange={(e) => update("differences", e.target.value)} className="bg-white/5 border-white/10 text-1 mt-1" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-2">Cancel</Button>
          <Button data-testid="comp-form-save" onClick={submit} disabled={saving} className="cta-primary">
            {saving ? "Saving…" : "Save comp"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ComparisonBoardPage() {
  const { user } = useAuth();
  const playerId = useResolvedPlayerId();
  const isAdmin = user?.role === "admin";
  const [player, setPlayer] = useState(null);
  const [comps, setComps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorSlot, setEditorSlot] = useState(null);

  const load = async () => {
    if (!playerId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        api.get(`/players/${playerId}`),
        api.get("/comparisons", { params: { player_id: playerId } }),
      ]);
      setPlayer(pRes.data);
      setComps(cRes.data || []);
    } catch (e) {
      toast.error("Failed to load comparisons");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [playerId]);

  const compFor = (key) => comps.find((c) => c.slot === key) || null;

  const onSave = async (payload) => {
    try {
      await api.post("/comparisons", {
        player_id: playerId,
        slot: editorSlot.key,
        ...payload,
      });
      toast.success(`${editorSlot.label} comp saved`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const onDelete = async (comp) => {
    if (!comp) return;
    if (!window.confirm("Delete this comparison?")) return;
    try {
      await api.delete(`/comparisons/${comp.id}`);
      toast.success("Comp removed");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (loading) return <LoadingState />;

  return (
    <div className="max-w-[1500px] mx-auto">
      <PlayerSubNav />
      <PageHeader
        title="NFL Comparison Board"
        subtitle={
          player
            ? `Three NFL careers ${player.full_name}'s tape projects to. Bet on Realistic. Plan for Floor. Push for Ceiling.`
            : "Three NFL careers his tape projects to. Bet on Realistic. Plan for Floor. Push for Ceiling."
        }
      />

      {!player && (
        <GlassCard className="p-6 text-2 text-sm" testId="comp-no-player">
          No player linked to your account yet.
        </GlassCard>
      )}

      {player && (
        <div className="grid grid-cols-12 gap-5">
          {/* Prospect summary banner */}
          <GlassCard className="col-span-12 p-5" testId="comp-prospect-banner">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 rounded-full bg-gradient-to-br from-cyan-400 to-blue-500 grid place-items-center text-[#04111A] font-semibold">
                  {(player.full_name || "?").slice(0, 1)}
                </div>
                <div>
                  <div className="text-1 font-semibold">{player.full_name}</div>
                  <div className="text-3 text-xs">{player.position} · {player.school || "—"} · {player.class_year || "—"}</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusPill tone="cyan">{player.position}</StatusPill>
                {player.archetype && <StatusPill tone="ice">{player.archetype}</StatusPill>}
              </div>
              <div className="flex items-center gap-3 text-[11px] text-3">
                <span className="flex items-center gap-1.5"><Ruler className="h-3 w-3" /> {player.measurables?.height || "—"}</span>
                <span className="flex items-center gap-1.5"><Weight className="h-3 w-3" /> {player.measurables?.weight || "—"}</span>
                <span className="flex items-center gap-1.5"><Timer className="h-3 w-3" /> {player.measurables?.forty_yard || "—"}</span>
                {player.jersey_number && <span className="flex items-center gap-1.5"><Hash className="h-3 w-3" /> {player.jersey_number}</span>}
              </div>
            </div>
          </GlassCard>

          {SLOTS.map((slot) => {
            const c = compFor(slot.key);
            return (
              <CompCard
                key={slot.key}
                slot={slot}
                comp={c}
                isAdmin={isAdmin}
                onEdit={() => setEditorSlot(slot)}
                onDelete={() => onDelete(c)}
              />
            );
          })}

          {/* Side-by-side attribute table */}
          <GlassCard className="col-span-12 p-6" testId="comp-attributes-table">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-1 font-semibold">Attribute Comparison</h3>
                <div className="text-3 text-xs mt-0.5">Key measurables vs each NFL comp.</div>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-3 text-[11px] uppercase tracking-wider">
                    <th className="py-2 pr-4 font-medium">Attribute</th>
                    <th className="py-2 pr-4 font-medium">{player.full_name}</th>
                    {SLOTS.map((s) => (
                      <th key={s.key} className={`py-2 pr-4 font-medium ${s.text}`}>{s.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {[
                    { label: "Height", get: (p) => p?.height, prospect: player.measurables?.height },
                    { label: "Weight", get: (p) => p?.weight, prospect: player.measurables?.weight },
                    { label: "40-yard", get: (p) => p?.forty_yard, prospect: player.measurables?.forty_yard },
                    { label: "Position", get: (p) => p?.comp_position, prospect: player.position },
                    { label: "Team", get: (p) => p?.comp_team, prospect: player.school },
                    { label: "Draft Round", get: (p) => p?.comp_draft_round ? `R${p.comp_draft_round}` : null, prospect: player.projected_round || "TBD" },
                  ].map((row) => (
                    <tr key={row.label} className="text-2">
                      <td className="py-2.5 pr-4 text-3">{row.label}</td>
                      <td className="py-2.5 pr-4 text-1 font-mono">{row.prospect || "—"}</td>
                      {SLOTS.map((s) => {
                        const c = compFor(s.key);
                        return <td key={s.key} className="py-2.5 pr-4 font-mono">{c ? (row.get(c) || "—") : "—"}</td>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
        </div>
      )}

      <CompEditor
        open={!!editorSlot}
        onOpenChange={(o) => { if (!o) setEditorSlot(null); }}
        slot={editorSlot}
        comp={editorSlot ? compFor(editorSlot.key) : null}
        onSave={onSave}
      />
    </div>
  );
}
